import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../utils/AuthContext';
import type { EmployeeAwardGrant } from '../../types';
import {
  markAwardGrantSeen,
  pickNextUnseenAwardGrant,
  shouldShowAwardGrant,
  subscribeToUserAwardGrantsAsc,
} from '../../utils/employeeAchievements';
import EmployeeAwardPopup from './EmployeeAwardPopup';

/** Delay before showing so the popup doesn't collide with initial page load. */
const SHOW_DELAY_MS = 1500;

/**
 * Shows a one-time achievement unlock popup when the user has unseen grants.
 * Queues multiple grants oldest-first (same pattern as release announcements).
 */
const EmployeeAwardGate: React.FC = () => {
  const { user } = useAuth();
  const [grants, setGrants] = useState<EmployeeAwardGrant[]>([]);
  const [activeGrant, setActiveGrant] = useState<EmployeeAwardGrant | null>(null);
  const [visible, setVisible] = useState(false);
  const [seenGrantIds, setSeenGrantIds] = useState<string[]>([]);
  const pendingGrantIdRef = useRef<string | null>(null);
  const acknowledgedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user?.id) {
      setGrants([]);
      return;
    }
    return subscribeToUserAwardGrantsAsc(user.id, setGrants);
  }, [user?.id]);

  useEffect(() => {
    if (!user) {
      setActiveGrant(null);
      setVisible(false);
      pendingGrantIdRef.current = null;
      return;
    }

    const seenSet = new Set<string>([...(user.seenAwardGrantIds ?? []), ...seenGrantIds]);
    acknowledgedRef.current.forEach(id => seenSet.add(id));
    const seenIds = Array.from(seenSet);

    const next = pickNextUnseenAwardGrant(grants, seenIds);
    if (!next?.id || !shouldShowAwardGrant(next, user, seenIds)) {
      setActiveGrant(null);
      setVisible(false);
      pendingGrantIdRef.current = null;
      return;
    }

    setActiveGrant(next);

    if (pendingGrantIdRef.current === next.id) {
      return;
    }

    pendingGrantIdRef.current = next.id;
    setVisible(false);

    const timer = window.setTimeout(() => {
      setVisible(true);
    }, SHOW_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [grants, user, seenGrantIds]);

  const acknowledgeGrant = useCallback(
    (grantId: string) => {
      if (!grantId || acknowledgedRef.current.has(grantId)) return;
      acknowledgedRef.current.add(grantId);
      setSeenGrantIds(prev => (prev.includes(grantId) ? prev : [...prev, grantId]));
      if (user?.id) {
        markAwardGrantSeen(user.id, grantId).catch(err => {
          console.warn('Failed to record award grant dismissal:', err);
        });
      }
    },
    [user?.id],
  );

  const finishPopup = useCallback(() => {
    setVisible(false);
    setActiveGrant(null);
    pendingGrantIdRef.current = null;
  }, []);

  const handleAcknowledge = useCallback(() => {
    if (!activeGrant?.id) return;
    acknowledgeGrant(activeGrant.id);
  }, [activeGrant, acknowledgeGrant]);

  const handleFinish = useCallback(() => {
    finishPopup();
  }, [finishPopup]);

  const handleViewTrophyCase = useCallback(() => {
    if (!activeGrant?.id) return;
    acknowledgeGrant(activeGrant.id);
    finishPopup();
  }, [activeGrant, acknowledgeGrant, finishPopup]);

  if (!visible || !activeGrant) return null;

  return (
    <EmployeeAwardPopup
      grant={activeGrant}
      onAcknowledge={handleAcknowledge}
      onFinish={handleFinish}
      onViewTrophyCase={handleViewTrophyCase}
    />
  );
};

export default EmployeeAwardGate;
